import React from 'react'
import { Text, SimpleGrid, GridItem, Box } from '@chakra-ui/react'

import EmptyState from '~components/shared/empty-state'
import SharedStack from '~components/shared/wrappers/shared-stack'
import {
    PatientFieldsFragment,
    PracticeManagementSystem,
    PrescriptionFieldsFragment,
    useQueryPrescriptionsQuery,
} from '~graphql/generated/graphql'
import { useClinic } from '~contexts/clinic-context'
import { useFormatDate } from '~utils/hooks/use-format-date'
import { eqBoolFilter, eqStringFilter } from '~utils/query-helpers'
import LoadingView from '~components/shared/loading-view'
import { getPrescriptionValidityStatus } from '~utils/prescription-helpers'

interface Props {
    patient: PatientFieldsFragment
}

export default function PatientPrescriptions({ patient }: Props) {
    const { clinic } = useClinic()

    const { data, loading, error } = useQueryPrescriptionsQuery({
        variables: {
            where: [
                eqStringFilter('patient', patient.id),
                eqBoolFilter('is_active', true),
            ],
            pageSize: 50,
        },
        skip: !patient.id,
    })

    if (loading) {
        return <LoadingView />
    }

    if (error) {
        console.error('Error loading prescriptions:', error)
        return (
            <Box w="full" bg="gray.50" px={4} py={3} rounded="xl">
                <EmptyState
                    title="Error loading prescriptions"
                    subtitle=" "
                    py={6}
                />
            </Box>
        )
    }

    const prescriptions = data?.queryPrescriptions || []

    if (!prescriptions || prescriptions.length === 0) {
        return (
            <Box w="full" bg="gray.50" px={4} py={3} rounded="xl">
                <EmptyState title="No prescriptions" subtitle=" " py={6} />
            </Box>
        )
    }

    const showExpirationDate = clinic?.pms !== PracticeManagementSystem.EzyVet

    return (
        <Box w="full" bg="gray.50" px={4} py={3} rounded="xl">
            <SimpleGrid columns={4} mb={2} w="full">
                <GridItem colSpan={2}>
                    <Text
                        fontSize="xs"
                        textTransform="uppercase"
                        fontWeight="medium"
                    >
                        Prescription
                    </Text>
                </GridItem>
                {showExpirationDate && (
                    <Text
                        fontSize="xs"
                        textTransform="uppercase"
                        fontWeight="medium"
                        textAlign="right"
                    >
                        Expires
                    </Text>
                )}
                <Text
                    fontSize="xs"
                    textTransform="uppercase"
                    fontWeight="medium"
                    textAlign="right"
                >
                    Refills Left
                </Text>
            </SimpleGrid>
            {prescriptions.map(prescription => (
                <PrescriptionRecord
                    key={prescription.id}
                    prescription={prescription}
                />
            ))}
        </Box>
    )
}

interface PrescriptionProps {
    prescription: PrescriptionFieldsFragment
}

function PrescriptionRecord({ prescription }: PrescriptionProps) {
    const { clinic } = useClinic()
    const { formatDate } = useFormatDate()
    const showExpirationDate = clinic?.pms !== PracticeManagementSystem.EzyVet

    const { product, expiration_date, last_refill_time, remaining } =
        prescription
    const prescriptionName =
        product?.name || prescription.name || prescription?.product_name

    const unitSuffix = product?.volume
        ? ` ${product.volume}`
        : product?.unit
          ? ` ${product.unit}`
          : ''

    const isExpired = expiration_date && new Date(expiration_date) < new Date()

    const { usesRemaining, refillsLeft } =
        getPrescriptionValidityStatus(prescription)

    const statusBadge = usesRemaining
        ? {
              colorScheme: 'gray',
              label: `${remaining || 0}${unitSuffix} remaining`,
          }
        : {
              colorScheme: 'gray',
              label: `${refillsLeft} refills left`,
          }

    return (
        <SimpleGrid columns={4} mb={2} w="full">
            <GridItem colSpan={2}>
                <Text fontWeight="semibold" fontSize="sm">
                    {prescriptionName}
                </Text>
                {last_refill_time && (
                    <Text fontSize="xs" color="gray.500">
                        Last refill: {formatDate(last_refill_time)}
                    </Text>
                )}
            </GridItem>
            {showExpirationDate && (
                <Text
                    textAlign="right"
                    fontSize="sm"
                    color={isExpired ? 'red.500' : 'gray.500'}
                >
                    {expiration_date ? formatDate(expiration_date) : 'N/A'}
                </Text>
            )}
            <Text
                color={
                    usesRemaining || refillsLeft > 0 ? 'green.500' : 'red.500'
                }
                fontSize="sm"
                textAlign="right"
                fontWeight={
                    usesRemaining || refillsLeft > 0 ? 'semibold' : 'normal'
                }
            >
                {usesRemaining
                    ? `${remaining || 0}${unitSuffix} remaining`
                    : `${refillsLeft} refills left`}
            </Text>
        </SimpleGrid>
    )
}
